import {
  DEFAULT_DEMO_PERSONA_KEY,
  DEMO_PERSONAS,
  type DemoPersona,
  type DemoPersonaKey,
  resolveUserTeamRole,
  type TeamRole,
  type User,
} from '@indutrust/shared-model';

const DEMO_PERSONA_STORAGE_KEY = 'demo_persona';

function isDemoPersonaKey(value: unknown): value is DemoPersonaKey {
  return typeof value === 'string' && Object.hasOwn(DEMO_PERSONAS, value);
}

function readPersonaFromStorage(): DemoPersonaKey {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_DEMO_PERSONA_KEY;
  }
  const stored = localStorage.getItem(DEMO_PERSONA_STORAGE_KEY);
  return isDemoPersonaKey(stored) ? stored : DEFAULT_DEMO_PERSONA_KEY;
}

function writePersonaToStorage(key: DemoPersonaKey): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(DEMO_PERSONA_STORAGE_KEY, key);
}

let currentPersonaKey: DemoPersonaKey = readPersonaFromStorage();
let registrationUser: User | null = null;

/** User returned by demo `GET /auth` for the active persona. */
export function getDemoAuthUser(): User {
  // In the browser, re-read storage so Playwright `seedDemoPersona` (localStorage
  // only) stays aligned with an MSW worker that may outlive a single context.
  // Unit tests without `localStorage` keep the in-memory key from
  // {@link setDemoPersonaForTests}.
  if (typeof localStorage !== 'undefined') {
    currentPersonaKey = readPersonaFromStorage();
  }
  return registrationUser ?? DEMO_PERSONAS[currentPersonaKey].user;
}

/** Resolved intra-Company sub-role for MSW R5 cap enforcement. */
export function resolveDemoAuthUserTeamRole(): TeamRole {
  return resolveUserTeamRole(getDemoAuthUser());
}

/** Switches the active demo persona (also persisted to `localStorage`). */
export function setDemoPersonaKey(key: DemoPersonaKey): void {
  currentPersonaKey = key;
  writePersonaToStorage(key);
}

/** Overrides the auth user returned until the next persona switch / reset. */
export function setDemoRegistrationUser(user: User | null): void {
  registrationUser = user;
}

/** Test-only reset to the default admin persona. */
export function resetDemoPersonaForTests(): void {
  currentPersonaKey = DEFAULT_DEMO_PERSONA_KEY;
  registrationUser = null;
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(DEMO_PERSONA_STORAGE_KEY);
  }
}

/** Test-only pin of the active demo persona for behavioural MSW specs. */
export function setDemoPersonaForTests(key: DemoPersonaKey): void {
  currentPersonaKey = key;
  registrationUser = null;
  writePersonaToStorage(key);
}

/** Active demo persona metadata (email, user, company). */
export function getCurrentDemoPersona(): DemoPersona {
  if (typeof localStorage !== 'undefined') {
    currentPersonaKey = readPersonaFromStorage();
  }
  return DEMO_PERSONAS[currentPersonaKey];
}
